import axios from "axios";
import { api } from "@/auth/api";
import { settings } from "@/settings";
import { StaticTemplateRow } from "@/types";
import { headers, Paths, UON } from "@/utils";

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function isJasperTemplate(name?: string | null): boolean {
  return String(name ?? "").toLowerCase().includes(".jasper");
}

export function isPdfFile(file: File): boolean {
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".pdf") || file.type === "application/pdf";
}

export type StaticTemplateBulkMatchStatus = "matched" | "unmatched" | "duplicate" | "ignored";

export interface StaticTemplateBulkItem {
  id: string;
  fileName: string;
  file: File;
  templateCode: string | null;
  originalFileName: string | null;
  templateName: string | null;
  matchStatus: StaticTemplateBulkMatchStatus;
}

function fileBaseName(file: File): string {
  const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name || "";
  return relative.split(/[/\\]/).pop() || file.name;
}

function normalizeTemplateKey(value?: string | null): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9]+/g, "");
}

function codeInFileName(fileName: string, templateCode: string): boolean {
  const escaped = templateCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(fileName);
}

export function matchStaticTemplateFiles(
  files: File[],
  templates: StaticTemplateRow[]
): StaticTemplateBulkItem[] {
  const catalog = (templates || []).filter(
    (row): row is StaticTemplateRow & { templateCode: string } =>
      Boolean(row.templateCode) && !isJasperTemplate(row.templateName)
  );
  const usedCodes = new Set<string>();
  const items: StaticTemplateBulkItem[] = [];

  files.forEach((file, index) => {
    const fileName = fileBaseName(file);
    const base: StaticTemplateBulkItem = {
      id: `${index}-${fileName}`,
      fileName,
      file,
      templateCode: null,
      originalFileName: null,
      templateName: null,
      matchStatus: "unmatched",
    };
    if (!isPdfFile(file)) {
      base.matchStatus = "ignored";
      items.push(base);
      return;
    }

    const codeHits = catalog.filter((row) => codeInFileName(fileName, row.templateCode));
    let chosen: (StaticTemplateRow & { templateCode: string }) | null = null;
    if (codeHits.length === 1) {
      chosen = codeHits[0];
    } else if (codeHits.length === 0) {
      const fileKey = normalizeTemplateKey(fileName);
      const nameHits = catalog.filter((row) => fileKey && normalizeTemplateKey(row.templateName) === fileKey);
      if (nameHits.length === 1) {
        chosen = nameHits[0];
      }
    }

    if (!chosen) {
      items.push(base);
      return;
    }
    if (usedCodes.has(chosen.templateCode)) {
      base.templateCode = chosen.templateCode;
      base.originalFileName = toOriginalFileName(chosen.templateName);
      base.templateName = chosen.templateName;
      base.matchStatus = "duplicate";
      items.push(base);
      return;
    }
    usedCodes.add(chosen.templateCode);
    base.templateCode = chosen.templateCode;
    base.originalFileName = toOriginalFileName(chosen.templateName);
    base.templateName = chosen.templateName;
    base.matchStatus = "matched";
    items.push(base);
  });

  const order = { matched: 0, duplicate: 1, unmatched: 2, ignored: 3 };
  return items.sort((left, right) => order[left.matchStatus] - order[right.matchStatus] || left.fileName.localeCompare(right.fileName));
}

export function toOriginalFileName(name?: string | null): string {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\.[^.]+$/, "") || trimmed;
}

export function resolveOriginalFileName(
  rows: StaticTemplateRow[],
  templateCode?: string | null,
  fallbackFileName?: string | null
): string {
  const match = (rows || []).find((row) => row.templateCode && row.templateCode === templateCode);
  return toOriginalFileName(match?.templateName) || toOriginalFileName(fallbackFileName);
}

export function readFileAsBytes(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (result instanceof ArrayBuffer) {
        resolve(new Uint8Array(result));
        return;
      }
      reject(new Error("empty file"));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export function buildPrintFileBody(input: {
  templateCode: string;
  originalFileName: string;
  content: string;
}) {
  return {
    template_code: input.templateCode,
    originalFileName: input.originalFileName,
    format: "application/pdf",
    relations: {
      items: [
        {
          objectUON: {
            objectType: "odm.filemngt.file",
            systemUid: UON.SystemUid.odmFileManagement,
          },
        },
      ],
    },
    content: input.content,
  };
}

function base64ToBytes(encoded: string): Uint8Array {
  const raw = encoded.includes(",") ? encoded.split(",").pop() || "" : encoded;
  const binary = atob(raw.replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function ensurePdfName(name: string): string {
  const trimmed = name.trim() || "document";
  return /\.pdf$/i.test(trimmed) ? trimmed : `${trimmed}.pdf`;
}

function triggerDownload(bytes: Uint8Array, fileName: string, mimeType: string) {
  const blob = new Blob([bytes], { type: mimeType || "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function saveBase64File(content: string, fileName: string, mimeType?: string) {
  triggerDownload(base64ToBytes(content), ensurePdfName(fileName), mimeType || "application/pdf");
}

function decodePrintBody(data: ArrayBuffer, contentType: string): any {
  const bytes = new Uint8Array(data);
  if (!bytes.length) {
    return null;
  }
  const type = contentType.toLowerCase();
  const startsJson = bytes[0] === 0x7b || bytes[0] === 0x5b || bytes[0] === 0x22;
  if (type.includes("pdf") || type.includes("octet-stream")) {
    return { binary: bytes };
  }
  if (type.includes("json") || startsJson) {
    try {
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      return { binary: bytes };
    }
  }
  return { binary: bytes };
}

async function fetchManagedFile(resourceUid: string) {
  const systemUid = UON.SystemUid.odmFileManagement;
  const { data } = await api().get(`/${systemUid}/api/1/${systemUid}/file/${resourceUid}/`);
  return data;
}

export async function printStaticTemplate(
  payload: {
    templateCode: string;
    originalFileName: string;
    file: File;
  },
  options?: { saveFile?: boolean }
): Promise<{ downloaded: boolean; data: any }> {
  const saveFile = options?.saveFile !== false;
  const bytes = await readFileAsBytes(payload.file);
  const response = await api().post(
    `/${UON.SystemUid.odmOrchestration}/business-api/1/${Paths.printFile}/`,
    buildPrintFileBody({
      templateCode: payload.templateCode,
      originalFileName: payload.originalFileName,
      content: bytesToBase64(bytes),
    }),
    { timeout: 0, responseType: "arraybuffer" }
  );
  const contentType = String(response.headers?.["content-type"] || "");
  let data = decodePrintBody(response.data, contentType);
  let content = typeof data === "string" ? data : data?.content;
  let fileName = (typeof data === "object" && data?.originalFileName) || payload.originalFileName;
  let format = (typeof data === "object" && data?.format) || "application/pdf";

  if (!content && data?.binary) {
    if (saveFile) {
      triggerDownload(data.binary, ensurePdfName(fileName), contentType || "application/pdf");
    }
    return { downloaded: true, data };
  }

  if (!content && data?.resourceUid) {
    const file = await fetchManagedFile(data.resourceUid);
    content = file?.content;
    fileName = file?.originalFileName || fileName;
    format = file?.format || format;
    data = file || data;
  }

  if (typeof content === "string" && content) {
    if (saveFile) {
      saveBase64File(content, fileName, format);
    }
    return { downloaded: true, data };
  }

  return { downloaded: false, data };
}

export async function createStaticTemplate(payload: {
  templateCode: string;
  file: File;
  originalFileName?: string;
  saveFile?: boolean;
}) {
  const originalFileName =
    toOriginalFileName(payload.originalFileName) || toOriginalFileName(payload.file.name);
  const result = await printStaticTemplate(
    {
      templateCode: payload.templateCode,
      originalFileName,
      file: payload.file,
    },
    { saveFile: payload.saveFile }
  );
  return result;
}

function mapPrintReportRow(template: any, index: number): StaticTemplateRow {
  return {
    id: index + 1,
    description: template?._description ?? null,
    templateCode: template?.template_code ?? null,
    templateName: template?.template_name ?? null,
    active: null,
    documentType: template?.document_type ?? null,
  };
}

export async function fetchStaticTemplates(): Promise<StaticTemplateRow[]> {
  const qc = {
    queryId: "print-report-template",
    limit: 500,
  };
  const requestHeaders = headers({ qc: JSON.stringify(qc) });
  if (!requestHeaders) {
    return [];
  }
  const response = await axios.get(settings.api_query_url, {
    headers: requestHeaders,
  });
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows
    .filter((template) => !isJasperTemplate(template?.template_name))
    .map((template, index) => mapPrintReportRow(template, index));
}

function matchesLike(value: string | number | null | undefined, filter?: string): boolean {
  if (!filter) {
    return true;
  }
  return String(value ?? "")
    .toLowerCase()
    .includes(String(filter).toLowerCase());
}

export function queryStaticTemplates(
  source: StaticTemplateRow[],
  filter: any = {},
  offset = 0,
  limit = 10
): { data: StaticTemplateRow[]; count: number } {
  const rows = (source || []).filter(
    (item) =>
      matchesLike(item.id, filter.id) &&
      matchesLike(item.description, filter.description) &&
      matchesLike(item.templateCode, filter.templateCode) &&
      matchesLike(item.templateName, filter.templateName) &&
      matchesLike(item.documentType, filter.documentType)
  );
  const start = offset || 0;
  const size = limit || 10;
  return {
    data: rows.slice(start, start + size),
    count: rows.length,
  };
}

export { bytesToBase64 };
